import React, { useState, useEffect, useContext, useMemo } from 'react';
import { StockComparisonContext } from '../contexts/StockComparisonContext';
import MetricsTable from '../components/MetricsTable';
import StockChart from '../components/StockComparisonChart';
import { fetchMetrics, fetchPublicFmpMetrics } from '../api';

import styles from './StockComparison.module.css';
import '../components/StockComparisonChart.css';

import { STOCKS } from '../constants/stocks';

export default function StockComparison() {
  const [loadingMetrics, setLoadingMetrics] = useState(false);
  const [loadingCharts, setLoadingCharts] = useState(false);

  // local symbol -> company name map (from Lambda list)
  const [nameMap, setNameMap] = useState({});

  const {
    selectedSymbols,
    setSelectedSymbols,
    selectedMetric,
    setSelectedMetric,
    metricsCache,
    setMetricsCache,
    chartCache,
    setChartCache
  } = useContext(StockComparisonContext);

  // Curated names from STOCKS (static)
  const STOCK_NAME_MAP = useMemo(() => {
    const m = new Map();
    STOCKS.forEach(({ symbol, name }) => m.set(symbol, name));
    return m;
  }, []); // STOCKS is static

  // Pull names from the same Lambda used by Header
  useEffect(() => {
    let cancelled = false;

    const loadList = async () => {
      const URL = 'https://1azyop0jo8.execute-api.eu-west-2.amazonaws.com/dev/list-stocks-search';
      try {
        const res = await fetch(URL);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = await res.json();

        const arr = Array.isArray(json) ? json : json?.items || [];
        const map = {};
        for (const it of arr) {
          const symbol = (it?.symbol || it?.ticker || '').toUpperCase();
          if (!symbol) continue;
          // Clean "Adobe - ADBE" => "Adobe"
          const rawName = it?.name || it?.companyName || symbol;
          const cleanName = typeof rawName === 'string'
            ? rawName.replace(/\s*-\s*[A-Z.]+$/, '').trim()
            : symbol;
          if (!map[symbol]) map[symbol] = cleanName;
        }
        if (!cancelled) setNameMap(map);
      } catch (e) {
        // silent fallback; STOCKS + metricsCache will still provide names for many
        console.warn('StockComparison: list-stocks-search fetch failed:', e);
      }
    };

    loadList();
    return () => { cancelled = true; };
  }, []);

  // Build selector options dynamically; resolve a friendly name
  const AVAILABLE_SYMBOLS = useMemo(() => {
    const resolveName = (symbol) =>
      nameMap[symbol] ||                               // from Lambda list (cleaned)
      metricsCache?.[symbol]?.companyName ||           // from metrics payload
      STOCK_NAME_MAP.get(symbol) ||                    // from curated static list
      symbol;                                          // fallback

    const syms = new Set([
      ...STOCKS.map(s => s.symbol),
      ...selectedSymbols,
      ...Object.keys(metricsCache || {}),
      ...Object.keys(chartCache || {})
    ]);

    const baseOrder = new Set(STOCKS.map(s => s.symbol));
    const base = STOCKS.map(({ symbol }) => ({
      symbol,
      name: resolveName(symbol)
    }));

    const extras = [...syms]
      .filter(s => !baseOrder.has(s))
      .sort()
      .map(symbol => ({ symbol, name: resolveName(symbol) }));

    return [...base, ...extras];
  }, [selectedSymbols, metricsCache, chartCache, STOCK_NAME_MAP, nameMap]);

  // Fetch latest (point-in-time) metrics for table (also can provide companyName)
  useEffect(() => {
    const abort = new AbortController();
    const load = async () => {
      const missing = selectedSymbols.filter(s => !metricsCache[s]);
      if (missing.length === 0) return;
      setLoadingMetrics(true);
      try {
        const results = await Promise.all(missing.map(symbol => fetchMetrics(symbol)));
        const merged = { ...metricsCache };
        const nameUpdates = {};
        missing.forEach((symbol, idx) => {
          const payload = results[idx];
          merged[symbol] = payload;
          // opportunistically capture a name if present
          const n = payload?.companyName || payload?.name || payload?.profile?.companyName;
          if (n && !nameMap[symbol]) {
            // sanitize if payload comes as "Name - TICKER"
            const clean = n.replace(/\s*-\s*[A-Z.]+$/, '').trim();
            nameUpdates[symbol] = clean;
          }
        });
        if (!abort.signal.aborted) {
          setMetricsCache(merged);
          if (Object.keys(nameUpdates).length) {
            setNameMap(prev => ({ ...prev, ...nameUpdates }));
          }
        }
      } catch (e) {
        if (!abort.signal.aborted) console.error('Error fetching metrics:', e);
      } finally {
        if (!abort.signal.aborted) setLoadingMetrics(false);
      }
    };
    load();
    return () => abort.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSymbols, metricsCache, setMetricsCache]);

  // Fetch full ratios history for the line chart
  useEffect(() => {
    const abort = new AbortController();
    const load = async () => {
      const missing = selectedSymbols.filter(s => !chartCache[s]);
      if (missing.length === 0) return;
      setLoadingCharts(true);
      try {
        const results = await Promise.all(
          missing.map(symbol => fetchPublicFmpMetrics(symbol, 'ratios'))
        );
        const merged = { ...chartCache };
        missing.forEach((symbol, idx) => {
          const ratioArray = results[idx];
          if (Array.isArray(ratioArray)) {
            merged[symbol] = ratioArray.map(entry => ({
              date: entry.date,
              // existing metrics
              priceEarningsRatio: entry.priceEarningsRatio,
              priceToSalesRatio: entry.priceToSalesRatio,
              priceToBookRatio: entry.priceToBookRatio,
              freeCashFlowOperatingCashFlowRatio: entry.freeCashFlowOperatingCashFlowRatio,
              netProfitMargin: entry.netProfitMargin,
              operatingProfitMargin: entry.operatingProfitMargin,
              // added metrics
              currentRatio: entry.currentRatio,
              quickRatio: entry.quickRatio,
              grossProfitMargin: entry.grossProfitMargin,
              returnOnAssets: entry.returnOnAssets,
              returnOnEquity: entry.returnOnEquity,
              debtEquityRatio: entry.debtEquityRatio,
              freeCashFlowPerShare: entry.freeCashFlowPerShare,
              priceToFreeCashFlowsRatio: entry.priceToFreeCashFlowsRatio
            }));
          } else {
            merged[symbol] = [];
          }
        });
        if (!abort.signal.aborted) setChartCache(merged);
      } catch (e) {
        if (!abort.signal.aborted) console.error('Error fetching ratios data:', e);
      } finally {
        if (!abort.signal.aborted) setLoadingCharts(false);
      }
    };
    load();
    return () => abort.abort();
  }, [selectedSymbols, chartCache, setChartCache]);

  const addStock = () => {
    if (selectedSymbols.length < 5) {
      const available = AVAILABLE_SYMBOLS
        .map(s => s.symbol)
        .filter(s => !selectedSymbols.includes(s));
      if (available.length > 0) {
        setSelectedSymbols([...selectedSymbols, available[0]]);
      }
    }
  };

  const removeStock = (index) => {
    const updated = [...selectedSymbols];
    updated.splice(index, 1);
    setSelectedSymbols(updated);
  };

  const updateStock = (index, newSymbol) => {
    const updated = [...selectedSymbols];
    updated[index] = newSymbol;
    setSelectedSymbols(updated);
  };

  const filteredSymbols = selectedSymbols;

  const metricsData = useMemo(() => {
    const out = {};
    filteredSymbols.forEach(s => {
      if (metricsCache[s]) out[s] = metricsCache[s];
    });
    return out;
  }, [filteredSymbols, metricsCache]);

  const chartData = useMemo(() => {
    const out = {};
    filteredSymbols.forEach(s => {
      if (chartCache[s]) out[s] = chartCache[s];
    });
    return out;
  }, [filteredSymbols, chartCache]);

  const isLoading = loadingMetrics || loadingCharts;

  return (
    <div className="App">
      <div className={styles.mainContainer}>
        <div className={styles.contentArea}>
          <div className={styles.comparisonContainer}>
            <h2> Stock Comparison (Up to 5)</h2>

            <div className={styles.stockSelectors}>
              {selectedSymbols.map((symbol, index) => (
                <div key={index} className={styles.stockSelector}>
                  <select
                    className={styles.stockSelect}
                    value={symbol}
                    onChange={(e) => updateStock(index, e.target.value)}
                  >
                    {AVAILABLE_SYMBOLS.map(({ symbol: ticker, name }) => (
                      <option key={ticker} value={ticker}>
                        {name}
                      </option>
                    ))}
                  </select>
                  {selectedSymbols.length > 1 && (
                    <button
                      className={styles.removeStockBtn}
                      onClick={() => removeStock(index)}
                    >
                      ✕
                    </button>
                  )}
                </div>
              ))}
            </div>

            {selectedSymbols.length < 5 && (
              <button className={styles.addStockBtn} onClick={addStock}>
                Quick Add Stock
              </button>
            )}
          </div>

          {isLoading && selectedSymbols.some(s => !metricsData[s]) ? (
            <p>Loading metrics...</p>
          ) : filteredSymbols.length === 0 ? (
            <div className={styles.metricsContainer}>
              <h2>Financial Metrics Comparison</h2>
              <div className={styles.noResults}>
                <p>No selected stock meets your filter criteria.</p>
              </div>
            </div>
          ) : (
            <div className="metrics-container">
              <h2>Financial Metrics Comparison</h2>
              <h4>Don't see a selected stock? Check your filters</h4>

              <div className={styles.stockChartContainer}>
                <StockChart
                  chartData={chartData}
                  symbols={filteredSymbols}
                  selectedMetric={selectedMetric}
                  setSelectedMetric={setSelectedMetric}
                />
              </div>

              <MetricsTable
                metricsData={metricsData}
                filteredSymbols={filteredSymbols}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
